import { useMemo, useState } from "react";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../api/auth";
import { getApiError } from "../api/errors";
import AppLogo from "../components/AppLogo";
import LanguageSelector from "../components/LanguageSelector";

function validatePassword(password) {
  if (password.length < 10) return "auth.tooShort";
  if (!/[a-z]/.test(password)) return "auth.lower";
  if (!/[A-Z]/.test(password)) return "auth.upper";
  if (!/\d/.test(password)) return "auth.number";
  return "";
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token")?.trim() || "", [searchParams]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!token) {
      setError(t("auth.invalidLink"));
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(t(passwordError));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("auth.mismatch"));
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword({ token, newPassword: password });
      setPassword("");
      setConfirmPassword("");
      setCompleted(true);
      navigate("/reset-password", { replace: true });
    } catch (requestError) {
      setError(
        getApiError(
          requestError,
          t("auth.invalidExpired"),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 1.5, sm: 3 },
        background:
          "radial-gradient(circle at 8% 12%, rgba(56,189,248,0.2), transparent 28%), radial-gradient(circle at 92% 15%, rgba(124,58,237,0.2), transparent 30%), #020617",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 540,
          borderRadius: 5,
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 36px 100px rgba(0,0,0,.42)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}><LanguageSelector /></Box>
          <AppLogo />

          {completed ? (
            <Stack spacing={2.25} alignItems="center" textAlign="center" sx={{ mt: 5 }}>
              <CheckCircleRoundedIcon color="success" sx={{ fontSize: 62 }} />
              <Typography variant="h4">{t("auth.passwordUpdated")}</Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {t("auth.passwordUpdatedBody")}
              </Typography>
              <Button component={Link} to="/login" variant="contained" fullWidth sx={{ mt: 1, py: 1.15 }}>
                {t("auth.signInNew")}
              </Button>
            </Stack>
          ) : (
            <>
              <Typography variant="h4" sx={{ mt: 5 }}>{t("auth.createPassword")}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, mb: 3, lineHeight: 1.7 }}>
                {t("auth.passwordRules")}
              </Typography>

              {!token && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t("auth.missingToken")}
                </Alert>
              )}
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  required
                  autoFocus
                  autoComplete="new-password"
                  label={t("auth.newPassword")}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  margin="normal"
                  inputProps={{ minLength: 10, maxLength: 128 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          aria-label={t(showPassword ? "auth.hidePassword" : "auth.showPassword")}
                          onClick={() => setShowPassword((current) => !current)}
                        >
                          {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  required
                  autoComplete="new-password"
                  label={t("auth.confirmPassword")}
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  margin="normal"
                  inputProps={{ minLength: 10, maxLength: 128 }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={submitting || !token}
                  sx={{ mt: 2, py: 1.15 }}
                >
                  {submitting ? <CircularProgress size={23} color="inherit" /> : t("auth.updatePassword")}
                </Button>
              </Box>

              <Typography textAlign="center" sx={{ mt: 2.5 }}>
                <Link to="/forgot-password" style={{ color: "#2563eb", fontWeight: 800 }}>
                  {t("auth.requestNew")}
                </Link>
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
